import React, { useState, useEffect } from "react";
import useAuth from "../../useAuth";
import UnAuthorized from "./UnAuthorized";
import PermissionLoading from "./PermissionLoading";
import { Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { server } from "../../server";

export default function RequireAuth({ component, userTypes, moduleCode }) {
  const { user, token } = useAuth();
  const location = useLocation();
  const [checkingPermission, setCheckingPermission] = useState(true);
  const [hasAccess, setHasAccess] = useState(null);

  const rawRole =
    typeof user?.role === "object"
      ? user?.role?.name
      : user?.role || user?.roleName || user?.userType || "";
  const roleName = (rawRole || "").toLowerCase();
  const roleId = user?.roleId || user?.role?.id;

  const isAdmin =
    roleId === 1 ||
    roleName === "admin" ||
    roleName === "superadmin" ||
    roleName === "administrator";

  useEffect(() => {
    let isMounted = true;

    const verifyDatabasePageAccess = async () => {
      // Unauthenticated or Admin bypass
      if (!user || isAdmin) {
        if (isMounted) setCheckingPermission(false);
        return;
      }

      try {
        setCheckingPermission(true);
        const authToken = token || localStorage.getItem("token");
        const res = await axios.get(`${server}/permissions/my-permissions`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        if (res.data?.success && Array.isArray(res.data?.data)) {
          const permList = res.data.data;
          const currentPath = location.pathname
            .replace(/^\/my\/?/, "")
            .toLowerCase();

          // Match by routePath, moduleCode, or path segment
          const matchedPerm = permList.find((p) => {
            const targetCode = (moduleCode || (currentPath === "" ? "DASHBOARD" : "")).toLowerCase();
            if (
              targetCode &&
              p.code &&
              p.code.toLowerCase() === targetCode
            ) {
              return true;
            }
            if (currentPath === "" && (p.routePath === "/my" || p.routePath === "/my/" || p.routePath === "")) {
              return true;
            }
            if (p.routePath) {
              const cleanRoute = p.routePath
                .replace(/^\/my\/?/, "")
                .toLowerCase();
              if (
                cleanRoute &&
                (currentPath === cleanRoute ||
                  currentPath.startsWith(cleanRoute + "/") ||
                  cleanRoute.startsWith(currentPath))
              ) {
                return true;
              }
            }
            if (p.code) {
              const codeLower = p.code.toLowerCase();
              if (
                currentPath &&
                (currentPath.includes(codeLower) ||
                  codeLower.includes(currentPath.split("/")[0]))
              ) {
                return true;
              }
            }
            return false;
          });

          if (matchedPerm) {
            setHasAccess(matchedPerm.canView === true);
          } else {
            setHasAccess(true);
          }
        } else {
          setHasAccess(true);
        }
      } catch (err) {
        console.error("Failed to verify database page access:", err);
        setHasAccess(true);
      } finally {
        if (isMounted) setCheckingPermission(false);
      }
    };

    verifyDatabasePageAccess();

    return () => {
      isMounted = false;
    };
  }, [
    user,
    token,
    location.pathname,
    moduleCode,
    isAdmin,
    roleName,
    userTypes,
  ]);

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (isAdmin) {
    return component;
  }

  if (checkingPermission) {
    return <PermissionLoading />;
  }

  if (hasAccess === false) {
    return <UnAuthorized />;
  }

  return component;
}
